export type CompressFormat = "jpeg" | "png";
export type OutputType = "base64" | "blob";


export type ConvertUrlToBase64Result = {
    width: number;
    height: number;
    dataUrl: string;
};

/**
 * Converte a dimensão de  imagem.
 * Baseado na mesma função do backend: ImageUtils.
 * @param img
 * @param mimetype
 * @param destDimension
 * @returns Uma nova imagem, em base64, no formato 16x9,
 */
function imgToCanvas(img: HTMLImageElement | HTMLCanvasElement, sourceDimension: ImageDimension, destDimension: ImageDimension, backgroundColor: string) {
    var sourceWidth = sourceDimension.width;
    var sourceHeight = sourceDimension.height;
    var sourceX = 0, sourceY = 0;

    var destWidth = destDimension.width;
    var destHeight = destDimension.height;
    var destX = 0, destY = 0;

    const baseRation = Math.round(((destDimension.width / destDimension.height) + Number.EPSILON) * 100) / 100;
    const invertBaseRation = Math.round(((destDimension.height / destDimension.width) + Number.EPSILON) * 100) / 100;
    const ratio = Math.round(((1.0 * sourceWidth / sourceHeight) + Number.EPSILON) * 100) / 100;

    if (sourceWidth <= destDimension.width && sourceHeight <= destDimension.height) {
        // Centralizar imagem no canvas.
        // Sem alterações (imagem pequena e sem resolução para resize).
        destWidth = sourceWidth;
        destHeight = sourceHeight;

        destX = (destDimension.width - destWidth) / 2;
        destY = (destDimension.height - destHeight) / 2;
    }
    else if (ratio > baseRation) {
        // Se aspect ratio  > 1,78 (16/9): selecionar um corte de: [altura * 16/9] x [altura] a partir do centro da imagem;
        // Fazer resize do corte resultante para 220x124px (vai perder faixa esquerda/direita e preservar altura)
        let newSourceWidth = sourceHeight * baseRation;
        sourceX = (sourceWidth - newSourceWidth) / 2;
        sourceWidth = newSourceWidth;
    }
    else if (ratio <= 1) {
        // Fazer resize de altura para 124px mantendo aspect ratio da imagem
        // e centralizá-la no canvas (sobrarão faixas na esquerda/direita).
        let p = 1.0 * destDimension.height / sourceHeight;
        destWidth = Math.round(sourceWidth * p);
        destHeight = Math.round(sourceHeight * p);

        destX = Math.round((destDimension.width - destWidth) / 2);
        destY = Math.round((destDimension.height - destHeight) / 2);
    }
    else if (ratio < baseRation) {
        // Se aspect ratio  < 1,78 (16/9): selecionar um corte de: [largura] x [largura * 9 / 16] a partir do centro da imagem;
        // Fazer resize do corte resultante para 220x124px (vai perder faixa superior/inferior e preservar largura)
        let newSourceHeight = sourceWidth * invertBaseRation;
        sourceY = (sourceHeight - newSourceHeight) / 2;
        sourceHeight = newSourceHeight;
    }

    let canvas = document.createElement("canvas");
    canvas.width = destDimension.width;
    canvas.height = destDimension.height;
    let context = canvas.getContext('2d');
    if (context != null) {
        //context.fillStyle = ThumbnailBackgroundColor;
        context.fillStyle = backgroundColor;
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, destX, destY, destWidth, destHeight);
    }
    return canvas;
}

/**
 * Converte uma imagem para base64.
 * @param img
 * @param mimetype
* @returns Uma nova imagem, em base64,
 */
function imgTocanvas(img: HTMLImageElement) {
    let canvas = document.createElement("canvas");
    let context = canvas.getContext('2d');
    if (context != null) {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        context.drawImage(img, 0, 0);
    }
    return canvas;
}

export class ImageUtils {
    /**
     * Carrega uma imagem e, se a imagem tiver dimensões menores do que o solicitado, converte a imagem em um thumbnail.
     * @param url Url da Imagem
     * @param mimetype Formato de Compressão
     * @param minDimension Dimensões mínimas
     * @returns Retorna a imagem em Base64.
     */
    static readImageWithMinimiumSize = (url: string, mimetype: string, minDimension: ImageDimension, backgroundColor: string): Promise<ConvertUrlToBase64Result> => {
        return new Promise<ConvertUrlToBase64Result>((resolve, reject) => {

            var img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                try {
                    let canvas = (img.width >= minDimension.width && img.height >= minDimension.height)
                        ? imgTocanvas(img)
                        : imgToCanvas(img, { width: img.naturalWidth, height: img.naturalHeight }, minDimension, backgroundColor);
                    resolve({
                        width: canvas.width,
                        height: canvas.height,
                        dataUrl: canvas.toDataURL(mimetype, 100)
                    });
                }
                catch {
                    reject("Cannot convert image to base64.");
                }
            };
            img.onerror = () => {
                reject("Load image error");
            };
            img.src = url;
        });
    };

    static readImage = (url: string, mimetype: string): Promise<ConvertUrlToBase64Result> => {
        return new Promise<ConvertUrlToBase64Result>((resolve, reject) => {

            var img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                try {
                    let canvas = imgTocanvas(img);
                    resolve({
                        width: canvas.width,
                        height: canvas.height,
                        dataUrl: canvas.toDataURL(mimetype, 100)
                    });
                }
                catch {
                    reject("Cannot convert image to base64.");
                }
            };
            img.onerror = () => {
                reject("Load image error");
            };
            img.src = url;
        });
    };


    /**
     * Converte um BLOB em Base64.
     * @param file
     */
    static blobToBase64 = (file: Blob, dimensions?: ImageDimension, adjustToRatio?: boolean): Promise<string> => {
        return new Promise<string>((resolve, error) => {
            const reader = new FileReader();
            if (file) {
                reader.readAsDataURL(file);
                reader.addEventListener("load", () => {
                    if (dimensions) {
                        var img = new Image();
                        img.onload = () => {
                            var imgWidth = img.width,
                                imgHeight = img.height;
                            var ratioWidth = imgWidth / dimensions.width,
                                ratioHeight = imgHeight / dimensions.height,
                                ratioAspect = ratioWidth > 1 ? ratioWidth : ratioHeight > 1 ? ratioHeight : 1;
                            var newWidth = imgWidth / ratioAspect,
                                newHeight = imgHeight / ratioAspect;
                            var cX = adjustToRatio == true ? newWidth : dimensions.width,
                                cY = adjustToRatio == true ? newHeight : dimensions.height;
                            var offsetX = (cX / 2) - (newWidth / 2),
                                offsetY = (cY / 2) - (newHeight / 2);

                            var canvas = document.createElement("canvas");
                            canvas.width = cX;
                            canvas.height = cY;
                            var context = canvas.getContext('2d');
                            if (context != null) {
                                context.fillStyle = 'transparent';
                                context.clearRect(0, 0, cX, cY);
                                context.fillRect(0, 0, cX, cY);
                                context.drawImage(img, offsetX, offsetY, newWidth, newHeight);
                                resolve(canvas.toDataURL(file.type, 100));
                            }
                        };
                        img.src = reader.result as string;
                    }
                    else {
                        resolve(reader.result as string);
                    }
                }
                );
                reader.onerror = (err: any) => {
                    error(err);
                };
            }
        });
    };

    /**
     * Converte um BLOB URL em uma Base64.
     * @param url
     */
    static blobUrlToBase64 = (url: string): Promise<string> => {

        return new Promise((resolve) => {

            var xhr = new XMLHttpRequest();
            xhr.responseType = 'blob';
            xhr.onload = () => {
                var recoveredBlob = xhr.response;
                var reader = new FileReader();
                reader.onload = () => {
                    resolve(reader.result as string);
                };
                reader.readAsDataURL(recoveredBlob);
            };
            xhr.open('GET', url);
            xhr.send();
        });
    };

    /**
     * Converte Base64 em um objeto FILE.
     * @param dataUrl
     * @param contentType
     */
    static imgBase64ToFile = (dataUrl: string, name: string = "file", contentType: string = "image/jpeg"): File => {
        return new File(ImageUtils.base64ToArray(dataUrl), name, { type: contentType });
    };

    /**
     * Converte Base64 em um objeto BLOB.
     * @param dataUrl
     * @param contentType
     */
    static imgBase64ToBlob = (dataUrl: string, contentType: string = "image/jpeg"): Blob => {
        return new Blob(ImageUtils.base64ToArray(dataUrl), { type: contentType });
    };

    /**
     * Converte um DataURL para um elemento do tipo Imagem.
     * @param urlOrDataUrl
     */
    static urlToHtmlImage = (urlOrDataUrl: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, error) => {
            var img = new Image();
            img.setAttribute('crossOrigin', 'anonymous');
            img.onload = () => {
                resolve(img);
            };
            img.onerror = (e: any) => {
                error(e);
                console.error(e);
            };
            img.src = urlOrDataUrl;
        });
    };

    /**
     * Converte um elemento CANVAS em BlobURL.
     * Este método usa URL.createObjectURL.
     * @param canvas
     * @param contentType
     * @param dataUrlToRevoke
     */
    static canvasToBlobUrl = (canvas: HTMLCanvasElement, contentType: string = "image/jpeg"): Promise<string> => {

        return new Promise((resolve, reject) => {

            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error('Canvas is empty'));
                    }
                    else {
                        resolve(window.URL.createObjectURL(blob));
                    }
                }, contentType);
        });
    };

    /**
     * Converte uma Imagem em array de bytes.
     * @param img
     */
    static imageToArray = (img: HTMLImageElement): BlobPart[] => {
        // Create an empty canvas element
        var canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        // Copy the image contents to the canvas
        var ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);

        // Get the data-URL formatted image
        // Firefox supports PNG and JPEG. You could check img.src to
        // guess the original format, but be aware the using "image/jpg"
        // will re-encode the image.
        var dataURL = canvas.toDataURL("image/png", 1);

        return ImageUtils.base64ToArray(dataURL);
    };

    /**
     * Converte Base64 em array de bytes.
     * @param dataUrl
     */
    static base64ToArray = (dataUrl: string): BlobPart[] => {

        // convert base64/URLEncoded data component to raw binary data held in a string
        var byteString;
        if (dataUrl.split(',')[0].indexOf('base64') >= 0)
            byteString = atob(dataUrl.split(',')[1]);
        else
            byteString = unescape(dataUrl.split(',')[1]);

        // write the bytes of the string to a typed array
        var ia = new Uint8Array(byteString.length);
        for (var i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        return [ia];
    };

    /**
     * Libera um objeto URL que foi criado préviamente chamando URL.createObjectURL()
     * @param dataUrl
     */
    static revokeObjectUrlIfBlob = (dataUrl: string): void => {

        if (dataUrl && dataUrl.indexOf("blob") == 0) {
            window.URL.revokeObjectURL(dataUrl);
        }
    };

    /**
     * Obtém um elemento FormData para upload do Blob como um arquivo.
     * @param blob
     * @param filename
     */
    static getBlobFormData = (blob: Blob, filename: string): FormData => {
        var fd = new FormData(document.forms[0]);
        fd.append('file', blob, filename);
        return fd;
    };
}

/**
 * Redimensionamento de imagens.
 */
export class Resizer {

    private static imageRotate(img: HTMLImageElement, deg = 0, dimension: ImageDimension, backgroundColor: string) {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        //Convert degrees to radian
        var rad = deg * Math.PI / 180;
        var ctx = canvas.getContext("2d");
        if (ctx != null) {
            //Set the origin to the center of the image
            ctx.translate(canvas.width / 2, canvas.height / 2);

            //Rotate the canvas around the origin
            ctx.rotate(rad);
            ctx.drawImage(img, -(img.width / 2), -(img.height / 2));
        }

        const canvas2 = document.createElement('canvas');
        canvas2.width = dimension.width;
        canvas2.height = dimension.height;
        var ctx2 = canvas2.getContext("2d");
        if (ctx2 != null) {
            ctx2.fillStyle = backgroundColor;
            ctx2.clearRect(0, 0, canvas2.width, canvas2.height);
            ctx2.fillRect(0, 0, canvas2.width, canvas2.height);
            ctx2.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, canvas2.width, canvas2.height);
        }

        return canvas2;
    }

    private static rotateAndCenter(img: HTMLImageElement, deg = 0) {
        const canvas = document.createElement('canvas');

        let width = img.naturalWidth;
        let height = img.naturalHeight;
        var x = 0, y = 0;
        if (deg === 90 || deg === 270) {
            canvas.width = height;
            canvas.height = width;
        } else {
            canvas.width = width;
            canvas.height = height;
        }

        //Convert degrees to radian
        var rad = deg * Math.PI / 180;
        var ctx = canvas.getContext("2d");
        if (ctx != null) {
            //Set the origin to the center of the image
            ctx.translate(canvas.width / 2, canvas.height / 2);

            //Rotate the canvas around the origin
            ctx.rotate(rad);

            //draw the image
            if (deg === 90 || deg === 270) {
                y = canvas.width;
                x = canvas.height;
            } else {
                x = canvas.width;
                y = canvas.height;
            }

            ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, width / -2, height / -2, x, y);
        }

        return canvas;
    }

    private static resizeAndRotateImage(img: HTMLImageElement, dimension: ImageDimension, mimetype: string = "image/jpeg", quality: number = 100, rotation = 0, backgroundColor: string): ConvertUrlToBase64Result {
        const qualityDecimal = quality / 100;

        let canvas: HTMLCanvasElement;

        if (rotation != 0) {
            if (img.naturalWidth <= dimension.width && img.naturalHeight <= dimension.height)
                canvas = this.imageRotate(img, rotation, dimension, backgroundColor);
            else {
                canvas = this.rotateAndCenter(img, rotation);
                canvas = imgToCanvas(canvas, { width: canvas.width, height: canvas.height }, dimension, backgroundColor);
            }
        }
        else
            canvas = imgToCanvas(img, { width: img.naturalWidth, height: img.naturalHeight }, dimension, backgroundColor);

        return {
            dataUrl: canvas.toDataURL(mimetype, qualityDecimal),
            width: canvas.width,
            height: canvas.height,
        };
    }

    private static resizeAndCoverImage(img: HTMLImageElement, dimension: ImageDimension, mimetype: string = "image/jpeg", quality: number = 100, rotation = 0): ConvertUrlToBase64Result {
        const qualityDecimal = quality / 100;

        var localImg: HTMLImageElement | HTMLCanvasElement = this.rotateAndCenter(img, rotation);

        let canvas: HTMLCanvasElement;
        canvas = document.createElement('canvas');
        canvas.width = dimension.width;
        canvas.height = dimension.height;
        const ctx = canvas.getContext('2d')!;

        // Aplica cover
        const canvasAspect = canvas.width / canvas.height;
        const imageAspect = img.width / img.height;

        let drawWidth, drawHeight, offsetX, offsetY;

        if (imageAspect > canvasAspect) {
            drawHeight = canvas.height;
            drawWidth = img.width * (canvas.height / img.height);
            offsetX = (canvas.width - drawWidth) / 2;
            offsetY = 0;
        } else {
            drawWidth = canvas.width;
            drawHeight = img.height * (canvas.width / img.width);
            offsetX = 0;
            offsetY = (canvas.height - drawHeight) / 2;
        }

        ctx.drawImage(localImg, offsetX, offsetY, drawWidth, drawHeight);

        //ctx.drawImage(localImg, 0, 0, dimension.width, dimension.height);


        return {
            dataUrl: canvas.toDataURL(mimetype, qualityDecimal),
            width: canvas.width,
            height: canvas.height,
        };
    }

    public createResizedImage(
        image: HTMLImageElement,
        dimension: ImageDimension,
        mimetype: string,
        quality: number,
        rotation: number,
        backgroundColor: string,
        onComplete: (data: ConvertUrlToBase64Result) => void,
    ) {
        if (image) {
            var resizedDataUrl = Resizer.resizeAndRotateImage(image, dimension, mimetype, quality, rotation, backgroundColor);
            onComplete(resizedDataUrl);
        }
    }

    public createCoverImage(
        image: HTMLImageElement,
        dimension: ImageDimension,
        mimetype: string,
        quality: number,
        rotation: number,
        onComplete: (data: ConvertUrlToBase64Result) => void,
    ) {
        if (image) {
            var resizedDataUrl = Resizer.resizeAndCoverImage(image, dimension, mimetype, quality, rotation);
            onComplete(resizedDataUrl);
        }
    }
}
